'use client';

import * as React from 'react';
import {X} from 'lucide-react';
import {Button} from '@purpose/ui/components/button/button';
import {
  Sheet,
  SheetContent,
  SheetTitle
} from '@purpose/ui/components/sheet/sheet';
import {useResponsive} from '@purpose/ui/hooks/useResponsive';
import {cn} from '@purpose/ui/lib/utils';
import {TFunction} from '@purpose/ui/utils/helpers';
import {VisuallyHiddenWrapper} from '../visually-hidden/visually-hidden';

type SidebarMenuItemProps = {
  id: number;
  icon: React.ReactNode;
  text: string;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  color?: string;
  isHidden?: boolean;
  disableCloseOnClick?: boolean;
  className?: string;
  hasYellowDot?: boolean;
};

type SidebarMenuProps = {
  open: boolean;
  onClose?: () => void;
  color?: string | null;
  menuItems?: SidebarMenuItemProps[];
  renderSectionUnderAvatar?: () => React.ReactNode;
  renderLanguagePicker?: () => React.ReactNode;
  renderAccountSwitch?: () => React.ReactNode;
  renderWorkspaces?: () => React.ReactNode;
};

export const SidebarMenuItem = ({
  icon,
  text,
  onClick,
  color,
  hasYellowDot,
  isHidden = false,
  className
}: SidebarMenuItemProps) => {
  if (isHidden) return null;

  return (
    <div
      onClick={onClick}
      className={cn(
        'flex h-11 cursor-pointer items-center gap-4.5 rounded-md px-4 transition-colors hover:bg-[rgba(53,62,75,0.47)]',
        className
      )}
    >
      <div className="relative text-white" style={{color: color || '#ffffff'}}>
        {icon}
        {hasYellowDot && (
          <div className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-yellow-500" />
        )}
      </div>
      <span className="text-sm text-white" style={{color: color || '#ffffff'}}>
        {text}
      </span>
    </div>
  );
};

const SidebarMenu = ({
  open,
  renderSectionUnderAvatar,
  renderLanguagePicker,
  renderAccountSwitch,
  renderWorkspaces,
  onClose,
  color,
  menuItems = []
}: SidebarMenuProps) => {
  const {isMobile, isTabletOrSmaller} = useResponsive();

  const handleMenuItemClick = (
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void,
    disableCloseOnClick?: boolean
  ) => {
    return (event: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(event);
      if (!disableCloseOnClick) {
        onClose?.();
      }
    };
  };

  // Handle outside click for desktop
  React.useEffect(() => {
    if (!open || isTabletOrSmaller) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('[data-sidebar-menu]')) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, isTabletOrSmaller, onClose]);

  // Lock body scroll when sidebar is open
  React.useEffect(() => {
    if (open) {
      // Store the current scroll position
      const scrollY = window.scrollY;

      // Lock the body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore scroll position and unlock body scroll
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  const sidebarContent = (
    <div
      data-sidebar-menu
      className={cn(
        'flex h-full w-full flex-col gap-4 py-4',
        isMobile ? 'px-3' : 'px-6'
      )}
      style={{backgroundColor: color || '#19212cff'}}
    >
      {/* Close button */}
      <div className="flex items-center p-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-auto p-0 text-white hover:bg-white/10"
          aria-label="Lukk"
        >
          <X className="h-6 w-6" />
        </Button>
      </div>

      {renderSectionUnderAvatar?.()}

      {/* Menu items */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        {menuItems.map(item => (
          <SidebarMenuItem
            key={item.id}
            {...item}
            onClick={handleMenuItemClick(
              item.onClick,
              item.disableCloseOnClick
            )}
          />
        ))}
        {renderWorkspaces?.()}
      </div>

      <div className="flex flex-col gap-2">
        {renderLanguagePicker?.()}
        {renderAccountSwitch?.()}
      </div>
    </div>
  );

  if (isTabletOrSmaller) {
    return (
      <Sheet open={open} onOpenChange={isOpen => !isOpen && onClose?.()}>
        <SheetContent
          side="left"
          className={cn(
            'z-9999 h-full max-h-screen border-0! p-0',
            isMobile ? 'w-full' : 'w-80'
          )}
        >
          <VisuallyHiddenWrapper>
            <SheetTitle>Menu</SheetTitle>
          </VisuallyHiddenWrapper>
          {sidebarContent}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <>
      {/* Backdrop for desktop */}
      {open && (
        <div className="fixed inset-0 z-9998 bg-black/50" onClick={onClose} />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-9999 transform transition-transform duration-200 ease-in-out',
          isMobile ? 'w-full' : 'w-80',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {sidebarContent}
      </div>
    </>
  );
};

export {SidebarMenu};
export type {SidebarMenuProps, SidebarMenuItemProps};
